// import { TableStructure } from '~components/TableComponent/types';
import { defineStore } from 'pinia';
// eslint-disable-next-line import/no-cycle
import Api from '~utils/Api';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '~store/index';
// eslint-disable-next-line import/no-cycle
import { useAuthStore } from '~store/auth/authModule';
// eslint-disable-next-line import/no-cycle
import { useOrgsStore } from '~store/orgs/orgsModule';
// eslint-disable-next-line import/no-cycle
import { usePatientStore } from '~store/patient/patientModule';
import {
  saveTreeState,
  restoreTreeState as restorePersistedState,
  clearTreeState,
} from '~utils/statePersistence';
import {
  TreeItem,
  StaffItem,
  TreeNode,
  SearchParams,
} from './types';

export const useTreeStore = defineStore('tree', {
  state: () => ({
    tree: null as any | TreeItem[],
    staffList: [] as any | StaffItem[],
    treeNode: '0' as string,
    selectedNodesMap: [] as TreeNode[],
    actualPatients: [] as any[],
    actualPatientsLength: null as number | null,
    nodeProcessing: false,
    isRestoring: false,
    typePost: false,
    underControl: null as any,
    pagin: {
      p_start: 0,
      p_lenght: 50,
    },
    patientSearch: '' as string,
    idPatientSearch: null as number | null,
    contractSearch: null as number | null,
  }),
  getters: {},
  actions: {
    async restoreTreeState(): Promise<void> {
      const authStore = useAuthStore();
      const userId = authStore.currentUser?.id_login;

      if (!userId) return;

      const persistedState = restorePersistedState(userId);

      if (!persistedState || !persistedState.selectedNodesIds.length) {
        await this.getOrgTreePart();
        return;
      }

      this.selectedNodesMap = [];
      this.treeNode = '0';
      this.typePost = false;
      this.nodeProcessing = true;
      this.isRestoring = true;

      try {
        await this.getOrgTreePart();

        // eslint-disable-next-line no-restricted-syntax
        for (const nodeId of persistedState.selectedNodesIds) {
          if (nodeId === '0') {
            // eslint-disable-next-line no-continue
            continue;
          }

          const currentLevel = this.tree || [];
          const foundNode = currentLevel.find(
            (node: any) => String(node.id_tree) === String(nodeId),
          );

          if (!foundNode) {
            break;
          }

          this.treeNode = nodeId;

          if (foundNode.id_type === 2) {
            break;
          }
          // eslint-disable-next-line no-await-in-loop
          await this.getOrgTreePart();
        }

        if (persistedState.currentNodeId && persistedState.currentNodeId !== '0') {
          this.treeNode = persistedState.currentNodeId;
        }

        const lastLevel = this.selectedNodesMap[this.selectedNodesMap.length - 1]?.nodeEntries;

        const finalNode = lastLevel?.find(
          (n: any) => String(n.id_tree) === String(this.treeNode),
        );

        if (finalNode?.id_type === 2) {
          this.typePost = true;
        }

        this.getTreeStaffList();
        this.getActualPatients(false, true);
      } finally {
        this.nodeProcessing = false;
        this.isRestoring = false;
      }
    },
    isNodeMapLoaded() {
      return this.selectedNodesMap.length;
    },
    checkInputType(value: string) {
      if (value === '') {
        return '';
      }
      const num = Number(value);
      return Number.isNaN(num) || value.trim() !== value ? 'string' : 'number';
    },

    async getOrgTreePart(): Promise<boolean> {
      const params = {
        Name_event: 'GET:tree_v2',
        Name_module: 'WEB_managers',
        id_tree: this.treeNode,
        id_org: useOrgsStore().currentOrg ?? 0,
        ...useMainStore().getConstantParams,
      };

      try {
        this.typePost = false;
        this.nodeProcessing = true;

        if (this.selectedNodesMap.length > 0) {
          const lastLevel = this.selectedNodesMap[this.selectedNodesMap.length - 1]?.nodeEntries;

          if (Array.isArray(lastLevel)) {
            const currentNode = lastLevel.find(
              (node: any) => String(node.id_tree) === String(this.treeNode),
            );

            if (currentNode?.id_type === 2) {
              this.tree = [];

              if (!this.isRestoring) {
                const userId = useAuthStore().currentUser.id_login;

                saveTreeState(
                  this.selectedNodesMap.map((n) => n.id_tree),
                  this.treeNode,
                  userId,
                );
              }
              this.typePost = true;
              return true;
            }
          }
        }

        const result = await Api.get(params);

        if (result.errorType) return false;

        const { data } = result;
        if (!data) return false;

        this.tree = data;

        const nodeExists = this.selectedNodesMap.some(
          (node) => String(node.id_tree) === String(this.treeNode),
        );

        if (!nodeExists) {
          this.selectedNodesMap.push({
            id_tree: this.treeNode,
            nodeEntries: this.tree,
          });

          if (!this.isRestoring) {
            const userId = useAuthStore().currentUser.id_login;

            saveTreeState(
              this.selectedNodesMap.map((n) => n.id_tree),
              this.treeNode,
              userId,
            );
          }
        }

        usePatientStore().getContractsMatrix(this.treeNode);
        this.getActualPatients(false, true);

        return true;
      } catch (err) {
        console.error(err);
        return false;
      } finally {
        this.nodeProcessing = false;
      }
    },

    async getTreeStaffList(search_string = '', type_drop = 0): Promise<void> {
      if (this.treeNode === '' || !this.selectedNodesMap.length) {
        return;
      }
      const params = {
        Name_event: 'GET:tree_staff_list',
        Name_module: 'WEB_managers',
        search_string,
        type_drop,
        // eslint-disable-next-line no-nested-ternary
        id_tree: this.treeNode !== '0' ? this.treeNode : this.selectedNodesMap[0].nodeEntries[0]?.id_tree,
        ...useMainStore().getConstantParams,
      };
      const result = await Api.get(params);
      if (!result.errorType) {
        const { data } = result;
        if (data) {
          data.forEach((i) => {
            const tmp = i?.id_patient ? JSON.parse(i?.id_patient) : 0;
            // eslint-disable-next-line no-param-reassign
            i.id_patient = tmp;
          });
          this.staffList = data;
          this.getActualPatients(false, true);
        }
      }
    },

    async getActualPatients(lazy = false, newNode = false): Promise<void> {
      if (
        (!useOrgsStore().currentOrg && !this.patientSearch)
      ) {
        return;
      }
      if (this.underControl) {
        this.underControl.abort();
      }
      if (newNode || this.patientSearch) {
        this.pagin.p_start = 0;
      }
      this.underControl = new AbortController();
      const { signal } = this.underControl;
      let searchParam = null;
      if (this.patientSearch) {
        searchParam = this.checkInputType(this.patientSearch.trim());
        if (searchParam === 'number') {
          this.idPatientSearch = parseInt(this.patientSearch, 10);
        }
      }
      const search_str: SearchParams = {
        fio: searchParam === 'string' ? this.patientSearch : '',
      };
      if (this.idPatientSearch) {
        search_str.id_patient = this.idPatientSearch;
      }
      const params: any = {
        Name_event: 'GET:patient_by_tree',
        Name_module: 'SmartPRO',
        id_org: useOrgsStore().currentOrg ?? 0,
        // eslint-disable-next-line no-nested-ternary
        // id_tree: (this.treeNode === '0' || !this.isNodeMapLoaded())
        //   ? this.treeNode : this.treeNode !== '0'
        // ? this.treeNode : this.selectedNodesMap[0].nodeEntries[0]?.id_tree,
        id_tree: this.treeNode,
        search_str: JSON.stringify({ ...search_str }),
        json_params: JSON.stringify({
          paging: {
            p_start: this.pagin.p_start,
            p_lenght: this.pagin.p_lenght,
            sort_type: 0,
          },
        }),
        ...useMainStore().getConstantParams,
      };
      if (this.contractSearch) {
        params.id_contract = this.contractSearch;
      }
      this.actualPatientsLength = null;
      try {
        const result = await Api.get(params, signal);
        if (!result.errorType) {
          const { data } = result;
          if (data) {
            const filtered = data.filter((i: any) => !!i.id_patient);
            if (lazy && (!newNode || this.patientSearch || this.idPatientSearch)) {
              this.actualPatients.push(...filtered);
              this.actualPatientsLength = data.length;
            } else if (!lazy && (newNode || this.patientSearch || this.idPatientSearch)) {
              this.actualPatients = filtered;
              this.actualPatientsLength = data.length;
            } else if (!lazy && !newNode) {
              this.actualPatients = filtered;
              this.actualPatientsLength = this.actualPatients.length;
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.idPatientSearch = null;
      }
    },

    setCurrentItem(item: string) {
      this.treeNode = item;
    },

    persistCurrentState(): void {
      const userId = useAuthStore().currentUser?.id_login;
      if (userId) {
        saveTreeState(
          this.selectedNodesMap.map((n) => n.id_tree),
          this.treeNode,
          userId,
        );
      }
    },

    clearNodeMap(): void {
      this.selectedNodesMap = [];
      const userId = useAuthStore().currentUser.id_login;
      if (userId) {
        clearTreeState(userId);
      }
    },

  },
});
